import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BarChart3, Users, Key, Activity, Settings, LogOut, Home, Shield, AlertTriangle, TrendingUp, User } from 'lucide-react';
import axios from 'axios';
import './SharedStyles.css';

interface AdminUser {
  id: string;
  email: string;
  plan: string;
  role?: string;
  subscriptionStatus: string;
  createdAt: string;
  lastLogin?: string;
  totalRequests?: number;
}

interface AdminStats {
  totalUsers: number;
  totalApiKeys: number;
  totalRequests: number;
  planDistribution: Record<string, number>;
  timestamp: string;
}

interface UserDetails {
  user: AdminUser;
  apiKeys: any[];
  usage: any;
}

type TabType = 'overview' | 'users' | 'analytics' | 'settings';

const AdminDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadAdminData();
  }, [user, navigate]);

  const loadAdminData = async () => {
    try {
      const [statsResponse, usersResponse] = await Promise.all([
        axios.get('/admin/stats'),
        axios.get('/admin/users')
      ]);

      setStats(statsResponse.data.data);
      setUsers(usersResponse.data.data.users);
    } catch (error: any) {
      console.error('Failed to load admin data:', error);
      setErrorMessage('Fehler beim Laden der Admin-Daten: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const loadUserDetails = async (userId: string) => {
    try {
      const response = await axios.get(`/admin/users/${userId}`);
      setSelectedUser(response.data.data);
    } catch (error: any) {
      setErrorMessage('Fehler beim Laden der Benutzerdetails: ' + (error.response?.data?.error || error.message));
    }
  };

  const updateUserPlan = async (email: string, plan: string) => {
    try {
      await axios.put('/admin/user-plan', { email, plan });
      setSuccessMessage(`Plan für ${email} erfolgreich auf ${plan} aktualisiert`);
      loadAdminData();
    } catch (error: any) {
      setErrorMessage('Fehler beim Aktualisieren des Plans: ' + (error.response?.data?.error || error.message));
    }
  };

  const updateUserRole = async (email: string, role: string) => {
    try {
      await axios.put('/admin/user-role', { email, role });
      setSuccessMessage(`Rolle für ${email} erfolgreich auf ${role} aktualisiert`);
      loadAdminData();
    } catch (error: any) {
      setErrorMessage('Fehler beim Aktualisieren der Rolle: ' + (error.response?.data?.error || error.message));
    }
  };

  const deactivateUserKeys = async (userId: string) => {
    try {
      await axios.put(`/admin/users/${userId}/deactivate-keys`);
      setSuccessMessage('API-Schlüssel erfolgreich deaktiviert');
      if (selectedUser && selectedUser.user.id === userId) {
        loadUserDetails(userId);
      }
    } catch (error: any) {
      setErrorMessage('Fehler beim Deaktivieren der API-Schlüssel: ' + (error.response?.data?.error || error.message));
    }
  };

  const getPlanBadgeStyle = (plan: string) => {
    switch (plan) {
      case 'free': return { background: 'var(--text-light)', color: 'white' };
      case 'starter': return { background: 'var(--success-green)', color: 'white' };
      case 'professional': return { background: 'var(--accent-blue)', color: 'white' };
      case 'business': return { background: 'var(--primary-medium)', color: 'white' };
      case 'enterprise': return { background: 'var(--text-dark)', color: 'white' };
      default: return { background: 'var(--text-light)', color: 'white' };
    }
  };

  const getRoleBadgeStyle = (role?: string) => {
    return role === 'admin' 
      ? { background: 'var(--error-red)', color: 'white' }
      : { background: 'var(--text-medium)', color: 'white' };
  };

  const tabs = [
    { id: 'overview', label: 'Übersicht', icon: Home },
    { id: 'users', label: 'Benutzer', icon: Users },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp },
    { id: 'settings', label: 'Einstellungen', icon: Settings }
  ];

  if (loading) {
    return (
      <div className="app-page">
        <div className="app-main flex items-center justify-center">
          <div className="text-center">
            <div className="app-loading" style={{ width: '3rem', height: '3rem', margin: '0 auto' }}></div>
            <p style={{ marginTop: '1rem', color: 'var(--text-medium)' }}>Admin-Dashboard wird geladen...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="app-page">
      {/* Header */}
      <header className="app-nav">
        <nav className="app-nav-content">
          <div className="flex items-center gap-4">
            <Link to="/" className="app-logo">
              Pfändungstabelle API
            </Link>
            <div className="flex items-center gap-2">
              <Shield size={16} style={{ color: 'var(--error-red)' }} />
              <span style={{ color: 'var(--error-red)', fontWeight: '600' }}>Admin Dashboard</span>
            </div>
          </div>
          <div className="app-nav-links">
            <Link
              to="/dashboard"
              className="app-button app-button-secondary flex items-center gap-2"
              style={{ textDecoration: 'none' }}
            >
              <User size={16} />
              Dashboard
            </Link>
            <span style={{ color: 'var(--text-medium)' }}>Admin: {user.email}</span>
            <button
              onClick={logout}
              className="app-button app-button-secondary flex items-center gap-2"
            >
              <LogOut size={16} />
              Abmelden
            </button>
          </div>
        </nav>
      </header>

      <main className="app-main">
        <div className="app-container">
          {/* Tab Navigation and Content Container */}
          <div style={{ 
            background: 'var(--background-white)',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            {/* Tab Navigation */}
            <div style={{ 
              borderBottom: '1px solid var(--border-light)', 
              padding: '0 1rem'
            }}>
              <div style={{ display: 'flex', gap: '0' }}>
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as TabType)}
                      style={{
                        padding: '1rem 1.5rem',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        color: activeTab === tab.id ? 'var(--voidmind-primary)' : 'var(--text-medium)',
                        borderBottom: activeTab === tab.id ? '2px solid var(--voidmind-primary)' : '2px solid transparent',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Icon size={18} />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tab Content */}
            <div style={{ padding: '2rem' }}>
              {activeTab === 'overview' && (
                <div>
                  <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '2rem', color: 'var(--text-dark)' }}>
                    System Übersicht
                  </h2>
                  
                  {/* Stats Cards */}
                  {stats && (
                    <div className="app-card-grid" style={{ marginBottom: '2rem' }}>
                      <div className="app-card">
                        <div className="flex items-center justify-between mb-4">
                          <div style={{ 
                            width: '60px', 
                            height: '60px', 
                            background: 'var(--voidmind-primary)', 
                            borderRadius: '8px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            color: 'white'
                          }}>
                            <Users size={24} />
                          </div>
                        </div>
                        <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-medium)', marginBottom: '0.5rem' }}>
                          Gesamte Benutzer
                        </h3>
                        <p style={{ fontSize: '2rem', fontWeight: '800', color: 'var(--text-dark)', marginBottom: '1rem' }}>
                          {stats.totalUsers.toLocaleString()}
                        </p>
                      </div>

                      <div className="app-card">
                        <div className="flex items-center justify-between mb-4">
                          <div style={{ 
                            width: '60px', 
                            height: '60px', 
                            background: 'var(--success-green)', 
                            borderRadius: '8px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            color: 'white'
                          }}>
                            <Key size={24} />
                          </div>
                        </div>
                        <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-medium)', marginBottom: '0.5rem' }}>
                          API-Schlüssel
                        </h3>
                        <p style={{ fontSize: '2rem', fontWeight: '800', color: 'var(--text-dark)', marginBottom: '1rem' }}>
                          {stats.totalApiKeys.toLocaleString()}
                        </p>
                      </div>

                      <div className="app-card">
                        <div className="flex items-center justify-between mb-4">
                          <div style={{ 
                            width: '60px', 
                            height: '60px', 
                            background: 'var(--accent-blue)', 
                            borderRadius: '8px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            color: 'white'
                          }}>
                            <Activity size={24} />
                          </div>
                        </div>
                        <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-medium)', marginBottom: '0.5rem' }}>
                          Gesamt-Anfragen
                        </h3>
                        <p style={{ fontSize: '2rem', fontWeight: '800', color: 'var(--text-dark)', marginBottom: '1rem' }}>
                          {stats.totalRequests.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Plan Distribution */}
                  {stats && (
                    <div>
                      <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--text-dark)' }}>
                        Plan-Verteilung
                      </h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                        {Object.entries(stats.planDistribution).map(([plan, count]) => (
                          <div key={plan} style={{ 
                            padding: '1.5rem', 
                            background: 'var(--background-light)', 
                            borderRadius: '8px', 
                            border: '1px solid var(--border-light)' 
                          }}>
                            <div className="flex items-center justify-between mb-2">
                              <h4 style={{ 
                                fontSize: '1rem', 
                                fontWeight: '600', 
                                color: 'var(--text-dark)', 
                                margin: 0,
                                textTransform: 'capitalize'
                              }}>
                                {plan}
                              </h4>
                              <span 
                                style={{
                                  ...getPlanBadgeStyle(plan),
                                  padding: '0.25rem 0.75rem',
                                  borderRadius: '12px',
                                  fontSize: '0.75rem',
                                  fontWeight: '600'
                                }}
                              >
                                {count}
                              </span>
                            </div>
                            <p style={{ 
                              fontSize: '0.875rem', 
                              color: 'var(--text-medium)', 
                              margin: 0 
                            }}>
                              {((count / stats.totalUsers) * 100).toFixed(1)}% der Benutzer
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'users' && (
                <div>
                  <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '2rem', color: 'var(--text-dark)' }}>
                    Benutzerverwaltung
                  </h2>
                  
                  {/* Users Table */}
                  <div style={{ 
                    background: 'var(--background-light)', 
                    borderRadius: '8px', 
                    border: '1px solid var(--border-light)',
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      padding: '1rem 1.5rem', 
                      borderBottom: '1px solid var(--border-light)',
                      background: 'var(--background-white)',
                      fontWeight: '600',
                      fontSize: '0.875rem',
                      color: 'var(--text-medium)',
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr',
                      gap: '1rem'
                    }}>
                      <span>E-Mail</span>
                      <span>Plan</span>
                      <span>Rolle</span>
                      <span>Status</span>
                      <span>API-Calls</span>
                      <span>Erstellt</span>
                      <span>Aktionen</span>
                    </div>
                    
                    {users.map((user) => (
                      <div 
                        key={user.id}
                        style={{ 
                          padding: '1rem 1.5rem', 
                          borderBottom: '1px solid var(--border-light)',
                          display: 'grid',
                          gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr',
                          gap: '1rem',
                          alignItems: 'center',
                          fontSize: '0.875rem'
                        }}
                      >
                        <span style={{ color: 'var(--text-dark)', fontWeight: '500' }}>
                          {user.email}
                        </span>
                        <span 
                          style={{
                            ...getPlanBadgeStyle(user.plan),
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            textTransform: 'capitalize',
                            display: 'inline-block'
                          }}
                        >
                          {user.plan}
                        </span>
                        <span 
                          style={{
                            ...getRoleBadgeStyle(user.role),
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            display: 'inline-block'
                          }}
                        >
                          {user.role || 'user'}
                        </span>
                        <span style={{ 
                          color: user.subscriptionStatus === 'active' ? 'var(--success-green)' : 'var(--text-medium)' 
                        }}>
                          {user.subscriptionStatus}
                        </span>
                        <span style={{ color: 'var(--text-medium)', fontWeight: '500' }}>
                          {user.totalRequests?.toLocaleString() || '0'}
                        </span>
                        <span style={{ color: 'var(--text-medium)' }}>
                          {new Date(user.createdAt).toLocaleDateString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </span>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => loadUserDetails(user.id)}
                            className="app-button app-button-secondary"
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.75rem' }}
                          >
                            Details
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* User Details Modal */}
                  {selectedUser && (
                    <div style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1000
                    }}>
                      <div style={{
                        backgroundColor: 'white',
                        padding: '2rem',
                        borderRadius: '8px',
                        maxWidth: '600px',
                        width: '90%',
                        maxHeight: '80vh',
                        overflow: 'auto'
                      }}>
                        <h3 style={{ margin: '0 0 1.5rem 0', color: 'var(--text-dark)' }}>
                          Benutzerdetails: {selectedUser.user.email}
                        </h3>
                        
                        <div style={{ marginBottom: '1.5rem' }}>
                          <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>Plan ändern:</h4>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {['free', 'starter', 'professional', 'business', 'enterprise'].map((plan) => (
                              <button
                                key={plan}
                                onClick={() => updateUserPlan(selectedUser.user.email, plan)}
                                className="app-button app-button-secondary"
                                style={{ 
                                  fontSize: '0.75rem', 
                                  padding: '0.25rem 0.75rem',
                                  background: selectedUser.user.plan === plan ? 'var(--voidmind-primary)' : undefined,
                                  color: selectedUser.user.plan === plan ? 'white' : undefined
                                }}
                              >
                                {plan}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div style={{ marginBottom: '1.5rem' }}>
                          <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>Rolle ändern:</h4>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {['user', 'admin'].map((role) => (
                              <button
                                key={role}
                                onClick={() => updateUserRole(selectedUser.user.email, role)}
                                className="app-button app-button-secondary"
                                style={{ 
                                  fontSize: '0.75rem', 
                                  padding: '0.25rem 0.75rem',
                                  background: (selectedUser.user.role || 'user') === role ? 'var(--voidmind-primary)' : undefined,
                                  color: (selectedUser.user.role || 'user') === role ? 'white' : undefined
                                }}
                              >
                                {role}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div style={{ marginBottom: '1.5rem' }}>
                          <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                            API-Nutzung:
                          </h4>
                          <div style={{ 
                            padding: '0.75rem', 
                            background: 'var(--background-light)', 
                            borderRadius: '4px',
                            marginBottom: '1rem',
                            fontSize: '0.875rem'
                          }}>
                            <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                              Gesamt-Anfragen: {selectedUser.usage?.totalRequests?.toLocaleString() || '0'}
                            </div>
                            <div style={{ color: 'var(--text-medium)' }}>
                              Verbleibendes Limit: {selectedUser.usage?.remaining?.toLocaleString() || 'Unbekannt'}
                            </div>
                          </div>
                        </div>

                        <div style={{ marginBottom: '1.5rem' }}>
                          <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                            API-Schlüssel ({selectedUser.apiKeys.length}):
                          </h4>
                          {selectedUser.apiKeys.length > 0 ? (
                            <div>
                              {selectedUser.apiKeys.map((key) => (
                                <div key={key.id} style={{ 
                                  padding: '0.5rem', 
                                  background: 'var(--background-light)', 
                                  borderRadius: '4px',
                                  marginBottom: '0.5rem',
                                  fontSize: '0.875rem'
                                }}>
                                  <div style={{ fontWeight: '500' }}>{key.name}</div>
                                  <div style={{ color: 'var(--text-medium)' }}>
                                    Status: {key.isActive ? 'Aktiv' : 'Inaktiv'}
                                  </div>
                                </div>
                              ))}
                              <button
                                onClick={() => deactivateUserKeys(selectedUser.user.id)}
                                className="app-button"
                                style={{ 
                                  background: 'var(--error-red)', 
                                  color: 'white',
                                  fontSize: '0.75rem',
                                  padding: '0.5rem 1rem',
                                  marginTop: '0.5rem'
                                }}
                              >
                                Alle Schlüssel deaktivieren
                              </button>
                            </div>
                          ) : (
                            <p style={{ color: 'var(--text-medium)', fontSize: '0.875rem' }}>
                              Keine API-Schlüssel vorhanden
                            </p>
                          )}
                        </div>

                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => setSelectedUser(null)}
                            className="app-button app-button-secondary"
                          >
                            Schließen
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'analytics' && (
                <div>
                  <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '2rem', color: 'var(--text-dark)' }}>
                    System Analytics
                  </h2>
                  
                  <div className="app-alert app-alert-info">
                    <p>Erweiterte Analytics-Features werden in einer zukünftigen Version verfügbar sein.</p>
                  </div>
                </div>
              )}

              {activeTab === 'settings' && (
                <div>
                  <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '2rem', color: 'var(--text-dark)' }}>
                    Admin-Einstellungen
                  </h2>
                  
                  <div className="app-alert app-alert-warning">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <AlertTriangle size={20} />
                      <strong>Wichtiger Hinweis</strong>
                    </div>
                    <p>Sie haben Admin-Zugriff auf das System. Verwenden Sie diese Funktionen verantwortungsvoll.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Success Message */}
      {successMessage && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: 'var(--success-green)',
          color: 'white',
          padding: '1rem',
          borderRadius: '8px',
          maxWidth: '400px',
          zIndex: 1000
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{successMessage}</span>
            <button
              onClick={() => setSuccessMessage(null)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                marginLeft: '1rem',
                fontSize: '1.2rem'
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: 'var(--error-red)',
          color: 'white',
          padding: '1rem',
          borderRadius: '8px',
          maxWidth: '400px',
          zIndex: 1000
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{errorMessage}</span>
            <button
              onClick={() => setErrorMessage(null)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                marginLeft: '1rem',
                fontSize: '1.2rem'
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
